/**
 * RenderZoneRenderer — projects video zone quads onto the Pannellum sphere
 * and renders YouTube/Vimeo iframes positioned over the panorama.
 *
 * Each render frame: project 4 [yaw,pitch] corners → screen [x,y], compute
 * a CSS matrix3d perspective transform (homography) from the iframe's native
 * pixel space to the projected screen quad.
 */

// muted=true  → silent autoplay (browser-safe, no user-gesture required)
// muted=false → autoplay with sound (requires prior user interaction)
function toEmbedUrl(url, autoplay = false, playerId = '', muted = false) {
    try {
        const u = new URL(url);
        let vid = u.searchParams.get('v');
        if (!vid && u.hostname === 'youtu.be') vid = u.pathname.slice(1);
        if (vid) {
            const ap = autoplay
                ? `&autoplay=1&mute=${muted ? 1 : 0}&enablejsapi=1&origin=${encodeURIComponent(window.location.origin)}`
                : '';
            return `https://www.youtube.com/embed/${vid}?rel=0${ap}`;
        }
        if (u.hostname.includes('vimeo.com')) {
            const id = u.pathname.split('/').filter(Boolean)[0];
            if (id) {
                if (autoplay) {
                    const muteParam = muted ? '&muted=1' : '';
                    const apiParam = playerId ? `&api=1&player_id=${encodeURIComponent(playerId)}` : '';
                    return `https://player.vimeo.com/video/${id}?autoplay=1${muteParam}${apiParam}`;
                }
                return `https://player.vimeo.com/video/${id}`;
            }
        }
    } catch (_) {}
    return null;
}

// Sort 4 screen points into [TL, TR, BR, BL] clockwise order
function sortQuad(pts) {
    var cx = (pts[0][0]+pts[1][0]+pts[2][0]+pts[3][0]) / 4;
    var cy = (pts[0][1]+pts[1][1]+pts[2][1]+pts[3][1]) / 4;
    var sorted = pts.slice().sort(function(a, b) {
        return Math.atan2(a[1]-cy, a[0]-cx) - Math.atan2(b[1]-cy, b[0]-cx);
    });
    var minIdx = 0;
    for (var i = 1; i < 4; i++) {
        if (sorted[i][0] + sorted[i][1] < sorted[minIdx][0] + sorted[minIdx][1]) minIdx = i;
    }
    var result = [];
    for (var i = 0; i < 4; i++) result.push(sorted[(minIdx + i) % 4]);
    return result; // [TL, TR, BR, BL]
}

/**
 * CSS matrix3d mapping a W×H source rect to 4 destination screen points.
 * Uses the direct Heckbert projective mapping formula — numerically stable.
 * dst = [TL, TR, BR, BL]
 *
 * The unit-square → dst homography H maps:
 *   (0,0)→TL  (1,0)→TR  (1,1)→BR  (0,1)→BL
 * Then we compose with scale(W,H) to map from [0,W]×[0,H].
 */
function computeMatrix3d(pts, W, H) {
    var d = sortQuad(pts);
    var x0=d[0][0], y0=d[0][1];  // TL
    var x1=d[1][0], y1=d[1][1];  // TR
    var x2=d[2][0], y2=d[2][1];  // BR
    var x3=d[3][0], y3=d[3][1];  // BL

    // Heckbert: dx1=TR-BR, dx2=BL-BR
    var dx1=x1-x2, dx2=x3-x2, dx3=x0-x1+x2-x3;
    var dy1=y1-y2, dy2=y3-y2, dy3=y0-y1+y2-y3;
    var det=dx1*dy2-dx2*dy1;
    var g=(dx3*dy2-dx2*dy3)/det;
    var h=(dx1*dy3-dx3*dy1)/det;

    // H maps unit square → dst; compose with scale(1/W, 1/H) for source rect
    var a=(x1-x0+g*x1)/W, b=(x3-x0+h*x3)/H, c=x0;
    var dd=(y1-y0+g*y1)/W, e=(y3-y0+h*y3)/H, f=y0;
    var ii=g/W, jj=h/H;

    // CSS matrix3d column-major 4×4 (z passthrough)
    return [a,dd,0,ii, b,e,0,jj, 0,0,1,0, c,f,0,1].join(',');
}

export default class RenderZoneRenderer {
    constructor(viewer, container, adminViewer, { mediaService } = {}) {
        this._viewer = viewer;
        this._container = container;
        this._adminViewer = adminViewer || null;
        this._mediaService = mediaService || null;
        this._onWindowBlur = this._onWindowBlur.bind(this);
        this._onWindowFocus = this._onWindowFocus.bind(this);
        this._zones = [];
        this._overlay = null;
        this._rafId = null;
        this._bound = this._update.bind(this);
        this._init();
    }

    _startLoop() {
        if (this._rafId) return;
        const tick = () => {
            this._update();
            this._rafId = requestAnimationFrame(tick);
        };
        this._rafId = requestAnimationFrame(tick);
    }

    _stopLoop() {
        if (this._rafId) {
            cancelAnimationFrame(this._rafId);
            this._rafId = null;
        }
    }

    _init() {
        this._overlay = document.createElement('div');
        this._overlay.style.cssText = [
            'position:absolute', 'top:0', 'left:0', 'width:100%', 'height:100%',
            'pointer-events:none', 'z-index:2',
        ].join(';');
        this._container.style.position = 'relative';
        this._container.appendChild(this._overlay);

        // Clicks inside a YouTube/Vimeo iframe never reach this document; the
        // window losing focus to that iframe is the only signal we get.
        window.addEventListener('blur', this._onWindowBlur);
        window.addEventListener('focus', this._onWindowFocus);

        this._viewer.on('render-zone-added', ({ uuid, url, type, sphereCoords }) => {
            this._addZone({ uuid, url, type, sphereCoords });
        });
        this._viewer.on('render-zone-removed', ({ uuid }) => {
            const idx = this._zones.findIndex(z => z.uuid === uuid);
            if (idx !== -1) this._removeZone(this._zones.splice(idx, 1)[0]);
        });
    }

    _onSceneLoad(render_zones_json) {
        this._stopLoop();
        this._clearZones();
        const json = render_zones_json || (this._viewer._imageData && this._viewer._imageData.render_zones_json);
        if (!json) return;
        try {
            JSON.parse(json).forEach(z => this._addZone(z));
        } catch (_) {}
    }

    _addZone({ uuid, url, type, sphereCoords }) {
        if (sphereCoords.length < 4 || !url) return;

        const isImage = (type === 'image');

        // Native size: 16:9 for video, square for image
        const W = 480, H = isImage ? 480 : 270;

        const el = document.createElement('div');
        el.style.cssText = [
            'position:absolute', 'top:0', 'left:0',
            `width:${W}px`, `height:${H}px`,
            'transform-origin:0 0',
            'pointer-events:auto',
            'display:none',
        ].join(';');
        el._vz_w = W;
        el._vz_hh = H;

        let detachMedia = null;
        if (isImage) {
            const img = document.createElement('img');
            img.src = url;
            img.style.cssText = 'width:100%;height:100%;object-fit:fill;display:block;';
            el.appendChild(img);
            el.addEventListener('click', () => this._focusZone({ sphereCoords }));
        } else {
            const embedUrl = toEmbedUrl(url, false, '', false);
            if (!embedUrl) return;
            const iframe = document.createElement('iframe');
            iframe.src = embedUrl;
            iframe.setAttribute('frameborder', '0');
            iframe.setAttribute('allow', 'autoplay; encrypted-media; fullscreen; picture-in-picture');
            iframe.style.cssText = 'width:100%;height:100%;border:none;display:block;';
            el.appendChild(iframe);
            el._vz_iframe = iframe;
            // Lets the media coordinator pause other tour media when the
            // visitor plays this zone (and pause the zone in turn).
            if (this._mediaService) detachMedia = this._mediaService.attachIframe(iframe);
        }

        if (this._viewer._editable && this._adminViewer) {
            const btn = document.createElement('div');
            btn.style.cssText = 'position:absolute;top:6px;right:6px;width:28px;height:28px;background:rgba(0,0,0,0.65);color:#fff;border-radius:50%;cursor:pointer;display:flex;align-items:center;justify-content:center;font-size:18px;line-height:1;z-index:10;';
            btn.textContent = '×';
            btn.addEventListener('click', (e) => {
                e.stopPropagation();
                const idx = this._zones.findIndex(z => z.uuid === uuid);
                if (idx !== -1) this._removeZone(this._zones.splice(idx, 1)[0]);
                const remaining = this._zones.map(z => ({ uuid: z.uuid, url: z.url, type: z.type, sphereCoords: z.sphereCoords }));
                this._adminViewer._fireEvent('render-zones.save', remaining);
            });
            el.appendChild(btn);
        }

        this._overlay.appendChild(el);
        this._zones.push({ uuid, url, type, sphereCoords, el, detachMedia, iframe: el._vz_iframe || null });
        this._startLoop();
    }

    _clearZones() {
        this._stopLoop();
        this._zones.forEach(z => this._removeZone(z));
        this._zones = [];
    }

    _removeZone(zone) {
        if (zone.detachMedia) zone.detachMedia();
        zone.el.parentNode && zone.el.parentNode.removeChild(zone.el);
    }

    // Turn the camera to the centre of a zone, like an info label on click.
    // Viewer only — in the editor clicks are for editing zones.
    _focusZone({ sphereCoords }) {
        if (this._viewer._editable || !sphereCoords || !sphereCoords.length) return;

        // sphereCoords are [yaw, pitch] in radians; average yaw on the circle
        // so zones crossing ±180° don't centre on the opposite side.
        let sin = 0, cos = 0, pitch = 0;
        sphereCoords.forEach(([y, p]) => {
            sin += Math.sin(y);
            cos += Math.cos(y);
            pitch += p;
        });
        const DEG = 180 / Math.PI;
        const targetYaw = Math.atan2(sin, cos) * DEG;
        const targetPitch = (pitch / sphereCoords.length) * DEG;

        this._viewer.setYaw(targetYaw, 1000);
        this._viewer.setPitch(targetPitch, 1000);
    }

    _onWindowBlur() {
        // activeElement switches to the iframe right after the blur event.
        setTimeout(() => {
            const zone = this._zones.find(z => z.iframe && z.iframe === document.activeElement);
            if (!zone || zone.focused) return;
            zone.focused = true;
            this._focusZone(zone);
        }, 0);
    }

    _onWindowFocus() {
        // Focus came back to the tour — the next click into a zone counts again.
        this._zones.forEach(z => { z.focused = false; });
    }

    // Project [yaw°, pitch°] → screen [x, y].
    // Returns null if the point is behind the camera (z ≤ 0).
    // Pass clamp=true to always return a point (z clamped to 0.001).
    _projectPoint(yawDeg, pitchDeg, clamp = false) {
        const v = this._viewer;
        if (!v._current || !v._renderer) return null;

        const R = Math.PI / 180;
        const pitch = pitchDeg * R, yaw = yawDeg * R;
        const cP = v._current.pitch * R, cY = v._current.yaw * R;

        const sinP = Math.sin(pitch), cosP = Math.cos(pitch);
        const sinCP = Math.sin(cP), cosCP = Math.cos(cP);
        const cosYdiff = Math.cos(-(yaw - cY));
        const sinYdiff = Math.sin(-(yaw - cY));

        const zRaw = sinP * sinCP + cosP * cosYdiff * cosCP;
        if (!clamp && zRaw <= 0) return null;
        const z = Math.max(zRaw, 0.001);

        let [cw, ch] = v._renderer.getCanvasDimensions();
        if (!v._disableDevicePixelRatio) {
            const dpr = window.devicePixelRatio || 1;
            cw /= dpr; ch /= dpr;
        }

        const hfovRes = v._normalizeHfov(v._current.hfov, cw / ch);
        const hfovTan = Math.tan((hfovRes[0] ? hfovRes[1] : v._current.hfov * R) / 2);

        const sx = -cw / hfovTan * sinYdiff * cosP / z / 2 + cw / 2;
        const sy = -cw / hfovTan * (sinP * cosCP - cosP * cosYdiff * sinCP) / z / 2 + ch / 2;
        return [sx, sy];
    }

    _update() {
        this._zones.forEach(({ sphereCoords, el }) => {
            const R = 180 / Math.PI;
            const pts = sphereCoords.map(([y, p]) => this._projectPoint(y * R, p * R));

            // Hide only when every corner is behind the camera
            if (pts.every(p => p === null)) {
                el.style.display = 'none';
                return;
            }

            // For corners behind the camera, use clamped projection so the
            // transform keeps updating instead of freezing.
            const safePts = sphereCoords.map(([y, p], i) =>
                pts[i] !== null ? pts[i] : this._projectPoint(y * R, p * R, true)
            );

            const m = computeMatrix3d(safePts, el._vz_w, el._vz_hh);
            el.style.display = 'block';
            el.style.transform = `matrix3d(${m})`;
        });
    }

    /**
     * Autoplay a video zone by UUID and call onDone when the video ends.
     * For image zones, onDone is called immediately and null is returned.
     * For video zones, returns a skip() function the caller can invoke to
     * stop playback early and trigger onDone immediately.
     */
    playZone(uuid, onDone) {
        const zone = this._zones.find(z => z.uuid === uuid);
        if (!zone) { onDone && onDone(); return null; }

        if (zone.type === 'image') {
            onDone && onDone();
            return null;
        }

        const iframe = zone.el.querySelector('iframe');
        if (!iframe) { onDone && onDone(); return null; }

        const playerId = 'vz_' + uuid.replace(/-/g, '').slice(0, 12);
        // Unmuted autoplay with API for completion detection
        const embedUrl = toEmbedUrl(zone.url, true, playerId, false);
        if (!embedUrl) { onDone && onDone(); return null; }

        const isYouTube = embedUrl.includes('youtube.com');
        const isVimeo   = embedUrl.includes('vimeo.com');

        let finished = false;
        let fallbackTimer = null;
        const finish = () => {
            if (finished) return;
            finished = true;
            if (fallbackTimer) { clearTimeout(fallbackTimer); fallbackTimer = null; }
            window.removeEventListener('message', onMessage);
            // Reset to muted autoplay so zone keeps looping after museum guide finishes
            iframe.src = toEmbedUrl(zone.url, !this._viewer._editable, '', true) || '';
            onDone && onDone();
        };

        fallbackTimer = setTimeout(finish, 30 * 60 * 1000);

        const onMessage = (evt) => {
            try {
                const data = typeof evt.data === 'string' ? JSON.parse(evt.data) : evt.data;
                if (!data) return;

                if (isYouTube && data.event === 'onStateChange' && data.info === 0) {
                    finish(); return;
                }
                if (isVimeo) {
                    if (data.event === 'ready') {
                        iframe.contentWindow.postMessage(JSON.stringify({
                            method: 'addEventListener', value: 'finish', player_id: playerId
                        }), 'https://player.vimeo.com');
                        return;
                    }
                    if (data.event === 'finish') finish();
                }
            } catch (_) {}
        };
        window.addEventListener('message', onMessage);
        iframe.src = embedUrl;
        return finish;
    }

    getZones() {
        return this._zones.map(({ uuid, url, type, sphereCoords }) => ({ uuid, url, type, sphereCoords }));
    }

    destroy() {
        window.removeEventListener('blur', this._onWindowBlur);
        window.removeEventListener('focus', this._onWindowFocus);
        this._stopLoop();
        this._clearZones();
        if (this._overlay && this._overlay.parentNode) this._overlay.parentNode.removeChild(this._overlay);
    }
}
